import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Switch,
  FormControlLabel,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { RootState, AppDispatch } from '../../store/store';
import {
  fetchAllTasks,
  fetchRunningTasks,
  executeTask,
  toggleTaskEnabled,
  setSelectedTask,
  fetchTaskLogs,
} from '../../store/slices/tasksSlice';
import TaskLogViewer from './TaskLogViewer';

const TasksPanel: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { tasks, runningTasks, loading, error, selectedTaskId } = useSelector((state: RootState) => state.tasks);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);

  // Initialize data on component mount
  useEffect(() => {
    dispatch(fetchAllTasks());
    dispatch(fetchRunningTasks());

    // Set up polling for running tasks (every 10 seconds)
    const intervalId = setInterval(() => {
      dispatch(fetchRunningTasks());
    }, 10000);

    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, [dispatch]);

  const handleForceRun = (taskId: string) => {
    dispatch(executeTask(taskId));
  };

  const handleToggleEnabled = (taskId: string, currentEnabled: boolean) => {
    dispatch(toggleTaskEnabled({ taskId, enabled: !currentEnabled }));
  };

  const handleExpandTask = (taskId: string) => {
    if (expandedTaskId === taskId) {
      setExpandedTaskId(null);
    } else {
      setExpandedTaskId(taskId);
    }
  };

  const handleViewLogs = (taskId: string) => {
    dispatch(setSelectedTask(taskId));
    dispatch(fetchTaskLogs({ taskId }));
  };

  const handleRefresh = () => {
    dispatch(fetchAllTasks());
    dispatch(fetchRunningTasks());
  };

  const isTaskRunning = (taskId: string): boolean => {
    if (!runningTasks || !runningTasks.tasks) return false;
    return runningTasks.tasks.some(task => task.taskId === taskId);
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h6'>
          Task Management
          {runningTasks && (
            <Typography component='span' variant='body2' sx={{ ml: 2, color: 'text.secondary' }}>
              ({runningTasks.count} running)
            </Typography>
          )}
        </Typography>
        <Tooltip title='Refresh tasks'>
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && tasks.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant='outlined' sx={{ maxHeight: '400px', overflow: 'auto' }}>
          <List dense>
            {tasks.length === 0 ? (
              <ListItem>
                <ListItemText primary='No tasks available' />
              </ListItem>
            ) : (
              tasks.map(task => (
                <React.Fragment key={task.id}>
                  <ListItem
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={task.enabled}
                              onChange={() => handleToggleEnabled(task.id, task.enabled)}
                              disabled={loading}
                              size='small'
                            />
                          }
                          label={task.enabled ? 'Enabled' : 'Disabled'}
                          sx={{ mr: 1 }}
                        />
                        <Tooltip title='Force run'>
                          <IconButton
                            edge='end'
                            onClick={() => handleForceRun(task.id)}
                            disabled={loading || isTaskRunning(task.id)}
                            color='primary'
                            size='small'
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                        <IconButton onClick={() => handleExpandTask(task.id)} size='small'>
                          {expandedTaskId === task.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant='body1'>
                            {task.name}
                            {isTaskRunning(task.id) && <CircularProgress size={12} sx={{ ml: 1 }} />}
                          </Typography>
                        </Box>
                      }
                      secondary={task.description || 'No description'}
                    />
                  </ListItem>
                  <Collapse in={expandedTaskId === task.id} timeout='auto' unmountOnExit>
                    <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>
                      <Typography variant='body2'>
                        <strong>Schedule:</strong> {task.cronExpression}
                      </Typography>
                      {task.lastRunTime && (
                        <Typography variant='body2'>
                          <strong>Last Run:</strong> {new Date(task.lastRunTime).toLocaleString()}
                        </Typography>
                      )}
                      {task.nextRunTime && (
                        <Typography variant='body2'>
                          <strong>Next Run:</strong> {new Date(task.nextRunTime).toLocaleString()}
                        </Typography>
                      )}
                      <Button variant='outlined' size='small' onClick={() => handleViewLogs(task.id)} sx={{ mt: 1 }}>
                        View Logs
                      </Button>
                    </Box>
                  </Collapse>
                  <Divider />
                </React.Fragment>
              ))
            )}
          </List>
        </Paper>
      )}

      {selectedTaskId && (
        <Box sx={{ mt: 3 }}>
          <TaskLogViewer />
        </Box>
      )}
    </Box>
  );
};

export default TasksPanel;
